import { Card, Heading, Tag, Tooltip } from "@digdir/designsystemet-react";
import { useTranslation } from "react-i18next";
import type {
  NorgesprisOppgjør,
  OppgjørForPeriode,
  StrømstønadOppgjør,
} from "@/api/generated";
import { KeyValueItem } from "@/components/KeyValueItem";
import { cn } from "@/utils";
import { formatDateString } from "@/utils/dateTimeUtils";

export const MaaledataPeriodeOppsummering = ({
  className,
  data,
  isTest,
}: {
  className?: string;
  data: OppgjørForPeriode;
  isTest?: boolean;
}) => {
  const { t } = useTranslation();

  const norgesprisOppgjør = data.norgesPrisOppgjør.filter(
    (o) => o.innlesningsType === "Normal",
  );
  const norgesprisOppgjørKorreksjoner = data.norgesPrisOppgjør.filter(
    (o) => o.innlesningsType !== "Normal",
  );
  const strømstønadOppgjør = data.strømstønadOppgjør.filter(
    (o) => o.innlesningsType === "Normal",
  );
  const strømstønadOppgjørKorreksjoner = data.strømstønadOppgjør.filter(
    (o) => o.innlesningsType !== "Normal",
  );
  const hasKorreksjoner =
    norgesprisOppgjørKorreksjoner.length > 0 ||
    strømstønadOppgjørKorreksjoner.length > 0;

  return (
    <div
      className={cn(
        `max-w-small-card md:max-w-full flex flex-col gap-4 justify-between`,
        className,
      )}
    >
      <div className="w-full">
        {isTest && (
          <Tag className="mb-2 bg-balder-support-surface-tinted! text-balder-text-support-default!">
            {t("pages.validering.test-oppsummering")}
          </Tag>
        )}
        <Card>
          <Heading>{t("pages.maaledata-periode.oppsummering.tittel")}</Heading>
          <span className="text-balder-text-subtle text-sm">
            {t("pages.maaledata-periode.oppsummering.sist-endret", {
              context: formatDateString(data.sistEndret, "short", true),
            })}
          </span>
          <dl className="mt-4 mb-0">
            <KeyValueItem
              label={t("pages.maaledata-periode.oppsummering.totalt-forbruk")}
              value={data.oppsummering.totaltForbrukKWh.toLocaleString("no-NO")}
              postfix="kWh"
            />
            <KeyValueItem
              label={t(
                "pages.maaledata-periode.oppsummering.stønadsberettiget-andel",
              )}
              value={data.oppsummering.stønadsberettigetAndelKWh.toLocaleString(
                "no-NO",
              )}
              postfix="kWh"
            />
            <KeyValueItem
              label={t("pages.maaledata-periode.oppsummering.antall-kunder")}
              value={data.oppsummering.totaltAntallKunder.toLocaleString(
                "no-NO",
              )}
            />
            <KeyValueItem
              label={t("pages.maaledata-periode.oppsummering.norgespriskunder")}
              value={data.oppsummering.norgesprisKunder.toLocaleString("no-NO")}
            />
            <KeyValueItem
              label={t(
                "pages.maaledata-periode.oppsummering.strømstønadskunder",
              )}
              value={data.oppsummering.strømstønadsKunder.toLocaleString(
                "no-NO",
              )}
            />
          </dl>
        </Card>
      </div>
      <div className="flex flex-col md:flex-row gap-4 align-top">
        <div className="flex-1 flex flex-col gap-4">
          {norgesprisOppgjør.map((oppgjør) => (
            <OppgjørCard
              key={oppgjør.ubetalingsReferanse}
              title={t(
                "pages.maaledata-periode.oppsummering.norgespris-oppgjør-tittel",
              )}
              oppgjør={oppgjør}
              isTest={isTest}
              className="w-full"
            />
          ))}
        </div>
        <div className="flex-1 flex flex-col gap-4">
          {strømstønadOppgjør.map((oppgjør) => (
            <OppgjørCard
              key={oppgjør.ubetalingsReferanse}
              title={t(
                "pages.maaledata-periode.oppsummering.strømstønad-oppgjør-tittel",
              )}
              oppgjør={oppgjør}
              isTest={isTest}
              className="w-full"
            />
          ))}
        </div>
      </div>
      {hasKorreksjoner && (
        <div className="mt-6">
          <Heading data-size="xs">
            {t("pages.maaledata-periode.oppsummering.korreksjoner_tittel")}
          </Heading>
          <div className="flex flex-col md:flex-row gap-4 align-top mt-4">
            <div className="flex-1 flex flex-col gap-4">
              {norgesprisOppgjørKorreksjoner.map((oppgjør) => (
                <OppgjørCard
                  key={oppgjør.ubetalingsReferanse}
                  title={t(
                    "pages.maaledata-periode.oppsummering.norgespris-oppgjør-tittel",
                  )}
                  oppgjør={oppgjør}
                  isTest={isTest}
                  className="w-full"
                />
              ))}
            </div>
            <div className="flex-1 flex flex-col gap-4">
              {strømstønadOppgjørKorreksjoner.map((oppgjør) => (
                <OppgjørCard
                  key={oppgjør.ubetalingsReferanse}
                  title={t(
                    "pages.maaledata-periode.oppsummering.strømstønad-oppgjør-tittel",
                  )}
                  oppgjør={oppgjør}
                  isTest={isTest}
                  className="w-full"
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OppgjørCard = ({
  title,
  oppgjør,
  isTest,
  className,
}: {
  title: string;
  oppgjør: StrømstønadOppgjør | NorgesprisOppgjør | null;
  isTest?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Card
      variant="tinted"
      className={cn(
        className,
        `flex flex-col gap-4 ${oppgjør?.innlesningsType === "Normal" ? "" : "bg-balder-background-warning"}`,
      )}
    >
      <div className="flex justify-between">
        <Heading>{title}</Heading>
        {oppgjør?.innlesningsType !== "Normal" && (
          <Tag variant="outline">
            {t(`pages.maaledata-periode.oppsummering.oppgjør_innlesningsType`, {
              context: oppgjør?.innlesningsType,
            })}
          </Tag>
        )}
      </div>
      <dl className="m-0">
        <KeyValueItem
          label={t("pages.maaledata-periode.oppsummering.grunnlag")}
          value={oppgjør?.støttetGrunnlagKWh.toLocaleString("no-NO")}
          postfix="kWh"
        />
        <KeyValueItem
          label={t("pages.maaledata-periode.oppsummering.oppgjør_status_label")}
          value={t("pages.maaledata-periode.oppsummering.oppgjørStatus", {
            context: oppgjør?.status,
          })}
        />
        {isTest !== true && (
          <KeyValueItem
            label={t("pages.maaledata-periode.oppsummering.utbetalingsdato")}
            value={
              formatDateString(oppgjør?.utbetalingsDato ?? undefined) ||
              t("pages.maaledata-periode.oppsummering.ubetalingsdato-ikkesatt")
            }
          />
        )}
        {isTest !== true && (
          <KeyValueItem
            label={t("pages.maaledata-periode.oppsummering.betalingsreferanse")}
            value={
              oppgjør?.ubetalingsReferanse ||
              t(
                "pages.maaledata-periode.oppsummering.betalingsreferanse-ikkesatt",
              )
            }
            align={oppgjør?.ubetalingsReferanse ? "column" : "row"}
          />
        )}
      </dl>

      <dl className="border rounded-md border-balder-border-subtle px-2 py-1 bg-balder-background-default mt-auto flex flex-wrap items-baseline gap-x-2">
        <div className="whitespace-nowrap">
          <KeyValueItem
            label={t("pages.maaledata-periode.oppsummering.oppgjørstype", {
              context: oppgjør?.oppgjørType,
            })}
            value={oppgjør?.sumTilUtbetalingMedMva.toLocaleString("no-NO")}
            postfix="kr"
          />
        </div>
        <Tooltip content={t("pages.maaledata-periode.oppsummering.hjelpMva")}>
          <span className="ms-auto text-sm text-balder-text-subtle whitespace-nowrap cursor-help">
            {t("pages.maaledata-periode.oppsummering.mvaBeløp")}
          </span>
        </Tooltip>
      </dl>
    </Card>
  );
};
